/**
 * Landmaschinen-Lexikon
 * ---------------------
 * Klassische Landtechnik + Smart-Farming-Zukunft. Jedes Item hat:
 *   id, title, era ('klassik' | 'smart' | 'zukunft'),
 *   category, summary, details (lange Erklärung), benefit (warum wichtig),
 *   brands (Liste), icon (Phosphor-Icon-Name), gradient (CSS-Farbverlauf).
 *
 * Statt Stockbilder verwenden wir deterministische Farbverläufe + Phosphor-
 * Icons. Das vermeidet Bild/Typ-Mismatches und lädt instant.
 */
export const MACHINE_ERAS = [
  {
    id: "klassik",
    title: "Klassische Landtechnik",
    subtitle: "Das Rückgrat jedes Betriebs",
    color: "#00FA9A",
  },
  {
    id: "smart",
    title: "Smart Farming (heute)",
    subtitle: "Was du ab 2026 schon auf dem Feld siehst",
    color: "#4DA3FF",
  },
  {
    id: "zukunft",
    title: "Zukunfts-Technologie",
    subtitle: "Prototypen & Serienstart in den nächsten 5 Jahren",
    color: "#FF7A45",
  },
];

export const MACHINES = [
  // ============ KLASSIK ============
  {
    id: "traktor", era: "klassik", category: "Zugmaschine", title: "Traktor",
    icon: "Tractor",
    gradient: "linear-gradient(135deg, #3E7034 0%, #00E68A 100%)",
    summary:
      "Universelle Zug- und Zapfwellenmaschine — seit über 100 Jahren das Herzstück der Landwirtschaft.",
    details:
      "Der Schlepper überträgt Kraft auf Boden-, Heu-, Stall- und Transportgeräte. Moderne Traktoren haben 70–600 PS, stufenlose Getriebe (CVT), gefederte Kabinen mit Klimaautomatik und einen ISOBUS-Anschluss, über den das Anbaugerät direkt mit dem Fahrercomputer spricht.",
    benefit:
      "Langlebig, reparierbar in jeder Werkstatt, bewährte Technik für alle Betriebsgrößen.",
    brands: ["Fendt", "John Deere", "Claas", "Case IH", "Massey Ferguson", "Steyr", "New Holland", "Deutz-Fahr"],
  },
  {
    id: "pflug", era: "klassik", category: "Bodenbearbeitung", title: "Pflug (Drehpflug)",
    icon: "GearSix",
    gradient: "linear-gradient(135deg, #4A5D3A 0%, #2D3F22 100%)",
    summary:
      "Wendet den Boden, gräbt Ernterückstände ein und schafft eine saubere Ausgangslage für die Saat.",
    details:
      "Der Drehpflug arbeitet meist 25–30 cm tief. Moderne Varianten sind Onland-fähig (auf unbearbeiteter Seite fahren, spart Bodenverdichtung) und per Isobus in der Höhe, Zugpunkt und Arbeitsbreite verstellbar.",
    benefit:
      "Erprobte Unkraut-Unterdrückung ohne Chemie — der Standard im biologischen Landbau.",
    brands: ["Lemken", "Kverneland", "Pöttinger", "Vogel & Noot"],
  },
  {
    id: "saemaschine", era: "klassik", category: "Saat", title: "Sämaschine",
    icon: "Plant",
    gradient: "linear-gradient(135deg, #6B8E5A 0%, #3E5A30 100%)",
    summary:
      "Legt das Saatgut in die gewünschte Tiefe, mit exakten Reihenabständen und Saatstärke.",
    details:
      "Mechanische und pneumatische Modelle (mit Gebläse). Einzelkornsaat (z.B. Mais, Rüben) oder Drillsaat (Getreide). Moderne Geräte verfügen über sektionsweise Abschaltung (GPS-gesteuert), um Überlappungen zu vermeiden.",
    benefit: "Gleichmäßiger Feldaufgang — die Basis jeder hohen Ernte.",
    brands: ["Amazone", "Horsch", "Kuhn", "Pöttinger"],
  },
  {
    id: "maehdrescher", era: "klassik", category: "Ernte", title: "Mähdrescher",
    icon: "Grains",
    gradient: "linear-gradient(135deg, #C0A048 0%, #E8CB6B 100%)",
    summary:
      "Mäht, drischt und reinigt Getreide, Raps und Körnermais in einem einzigen Arbeitsgang.",
    details:
      "Das Schneidwerk kann bis zu 12 m breit sein. Im Inneren werden Körner und Stroh getrennt, das Korn landet im Tank (bis 14.000 l), das Stroh wird geschwadet oder gehäckselt. Moderne Geräte messen Ertrag, Feuchte und Rohprotein pro Quadratmeter.",
    benefit: "Ermöglicht die Großflächen-Ernte mit 2–3 Personen Team — noch vor 100 Jahren waren es 30.",
    brands: ["Claas Lexion", "John Deere S-Series", "New Holland CR", "Case IH Axial-Flow"],
  },
  {
    id: "ballenpresse", era: "klassik", category: "Grünland", title: "Rund- & Quaderballenpresse",
    icon: "CirclesThreePlus",
    gradient: "linear-gradient(135deg, #A0885A 0%, #D9A87C 100%)",
    summary: "Presst Heu, Stroh oder Silage zu Rund- oder Quaderballen für einfache Lagerung und Transport.",
    details:
      "Rundballen (120–160 cm Durchmesser) sind für kleinere Betriebe praktisch. Quaderballen sind dichter gepresst und stapeln sich besser — ideal für Exporte. Kombi-Wickler-Pressen produzieren direkt silagefertige Wickelballen.",
    benefit: "Verwandelt 8 m³ lockeres Futter in einen 0.5 m³ kompakten Ballen — enorme Lagerersparnis.",
    brands: ["Krone", "Claas", "Kuhn", "Pöttinger", "Vicon"],
  },
  {
    id: "duengerstreuer", era: "klassik", category: "Düngung", title: "Düngerstreuer",
    icon: "Drop",
    gradient: "linear-gradient(135deg, #5C7A9E 0%, #2D4A6A 100%)",
    summary: "Verteilt Körner- oder Flüssigdünger gleichmäßig über das Feld.",
    details:
      "Scheibenstreuer (Zwei rotierende Teller) werfen Kornmineraldünger bis 36 m weit. Grenzstreu-Einrichtungen verhindern, dass Dünger in Gewässer gelangt. Moderne Geräte wiegen den Inhalt laufend und korrigieren die Dosis über GPS.",
    benefit: "Präzise Dosierung spart Geld und schützt die Umwelt.",
    brands: ["Amazone ZA-TS", "Kuhn Axis", "Rauch"],
  },

  // ============ SMART ============
  {
    id: "gps-lenksystem", era: "smart", category: "Navigation", title: "GPS-Lenksysteme (RTK)",
    icon: "Crosshair",
    gradient: "linear-gradient(135deg, #5A8AA8 0%, #2E5570 100%)",
    summary: "Der Traktor lenkt sich selbst — auf 2 cm genau, Tag und Nacht.",
    details:
      "Real-Time-Kinematic-GPS nutzt Signalkorrekturen einer Basisstation, um die Trecker-Position auf 2 cm zu bringen. Der Fahrer startet und überwacht, das System lenkt in parallelen Bahnen ohne Überlappung. Spart bis zu 8 % Betriebsmittel (Saatgut, Dünger, Spritzmittel).",
    benefit: "Weniger Überlappung = weniger Diesel, Saatgut und Pflanzenschutz. Gleichzeitig entlastet es die Augen des Fahrers auf langen Strecken.",
    brands: ["Trimble", "John Deere StarFire", "Topcon X35", "Fendt VarioGuide"],
  },
  {
    id: "section-control", era: "smart", category: "Präzision", title: "Section Control & Variable Rate",
    icon: "SlidersHorizontal",
    gradient: "linear-gradient(135deg, #4DA3FF 0%, #28495A 100%)",
    summary: "Jede Düse, jeder Säschar wird einzeln digital gesteuert — Überlappung wird unmöglich.",
    details:
      "Bei der Feldspritze schaltet jede einzelne Düse automatisch ab, sobald sie eine bereits behandelte Fläche erreicht. Bei der Sämaschine arbeiten einzelne Säaggregate unabhängig. Variable Rate Technology (VRT) passt zusätzlich die Ausbringmenge an die Bodenkarte an — auf dem lehmigen Bereich wird weniger gedüngt als im Sandnest.",
    benefit: "5–15 % Einsparung bei Dünger, Spritzmittel und Saatgut. Zusätzlicher Umweltnutzen.",
    brands: ["Amazone AmaSelect", "John Deere ExactApply", "Horsch SeedOn"],
  },
  {
    id: "drohne-multispektral", era: "smart", category: "Fernerkundung", title: "Multispektral-Drohne",
    icon: "Drone",
    gradient: "linear-gradient(135deg, #7A96AD 0%, #3E5568 100%)",
    summary: "Sieht deinem Bestand beim Wachsen zu — aus 100 m Höhe und mit Kamera-Augen, die Menschen nicht haben.",
    details:
      "Die Drohne fliegt autonom das Feld ab und fotografiert mit 5 Kamerabändern (Rot, Grün, Blau, Rot-Kante, Nahinfrarot). Daraus berechnet eine Software NDVI-Karten — feldinterne Heterogenität wird sichtbar. Gelbe Zonen im Maisfeld? Vielleicht Staunässe. Rote Zonen im Weizen? Womöglich Krankheit.",
    benefit: "Findet Probleme, bevor sie die Ernte mindern. Spätschaden-Management wird möglich.",
    brands: ["DJI Mavic 3 Multispectral", "Parrot Bluegrass", "Sentera 6X"],
  },
  {
    id: "sensornetz", era: "smart", category: "IoT", title: "IoT-Bodensensorik",
    icon: "WifiHigh",
    gradient: "linear-gradient(135deg, #6090A8 0%, #305060 100%)",
    summary: "Kleine Spieße im Boden melden Feuchte und Temperatur — direkt aufs Handy.",
    details:
      "Kapazitive Feuchtesensoren in 10/30/60 cm Tiefe messen stündlich. Ein LoRaWAN- oder NB-IoT-Gateway sendet die Werte über mehrere Kilometer. Dashboards warnen bei Trockenheit oder Staunässe. In Verbindung mit Bewässerungs-Ventilen entsteht eine Closed-Loop-Bewässerung.",
    benefit: "Trockenstress erkennen, bevor der Bestand welkt. Beregnung nur wenn nötig — spart Wasser & Energie.",
    brands: ["Sensoterra", "CropX", "AquaSpy", "agvolution"],
  },
  {
    id: "hack-roboter", era: "smart", category: "Pflanzenschutz", title: "Hack-Roboter",
    icon: "Robot",
    gradient: "linear-gradient(135deg, #4E7A63 0%, #244836 100%)",
    summary: "Ein elektrischer Roboter unterscheidet Unkraut von Kulturpflanze — und hackt nur das Unkraut weg.",
    details:
      "Kamerasysteme mit künstlicher Intelligenz scannen bei 2–5 km/h jede Reihe und steuern Werkzeuge (Hacken, Laser, punktgenaue Spritzpunkte). Funktioniert ab Erbstadium BBCH 14. Kann 24 h laufen und ist leise genug für nachtaktive Wildtiere.",
    benefit: "Bio-Betriebe gewinnen hier die größte Hebelwirkung: keine Glyphosat-Alternative nötig.",
    brands: ["Naïo Technologies Oz", "FarmDroid FD20", "Ekobot WEAI"],
  },
  {
    id: "satelliten-zonenkarten", era: "smart", category: "Daten", title: "Satelliten-Zonenkarten",
    icon: "Planet",
    gradient: "linear-gradient(135deg, #3E6A88 0%, #1F3A50 100%)",
    summary: "Kostenlose EU-Satellitendaten zeigen jedes Feld — alle 3–5 Tage mit 10 m Auflösung.",
    details:
      "Das Copernicus-Programm liefert Sentinel-2-Aufnahmen für ganz Europa gratis. Daraus werden NDVI-Zeitreihen, Bodenfeuchte (Sentinel-1) und Feldgrenzen extrahiert. Diese Karten werden direkt in die Traktor-Terminals übertragen, um die Düngung oder Aussaat zu steuern.",
    benefit: "Keine Flugkosten, keine Drohnenführerschein-Pflicht — nur eine Internetverbindung.",
    brands: ["Copernicus", "Bodenblick", "FarmInsight", "xarvio"],
  },

  // ============ ZUKUNFT ============
  {
    id: "autonomer-traktor", era: "zukunft", category: "Robotik", title: "Autonomer Traktor",
    icon: "Lightning",
    gradient: "linear-gradient(135deg, #FF7A45 0%, #FF7A45 100%)",
    summary: "Fahrerloser Schlepper, der 24/7 arbeitet und per App überwacht wird.",
    details:
      "John Deere zeigte 2022 den ersten voll autonomen 8R — Kameras, Lidar und RTK-GPS ersetzen den Fahrer. In Deutschland/Österreich fehlt noch der Rechtsrahmen, aber in den USA und Brasilien sind erste Serienmaschinen im Feld.",
    benefit: "Erledigt Nachteinsätze, entlastet Arbeitskräftemangel, reduziert Ermüdungsfehler.",
    brands: ["John Deere AutoTrac", "CNH Raven Industries", "Monarch Tractor"],
  },
  {
    id: "agri-photovoltaik", era: "zukunft", category: "Energie", title: "Agri-Photovoltaik",
    icon: "Sun",
    gradient: "linear-gradient(135deg, #FFC24B 0%, #FFC24B 100%)",
    summary: "Solarmodule über dem Feld — die Pflanzen wachsen darunter weiter.",
    details:
      "Hochgeständerte oder vertikale Solarmodule beschatten sensible Kulturen (Beeren, Apfelplantagen, Hopfen) und produzieren gleichzeitig Strom. Erste Anlagen in Bayern und Oberösterreich zeigen: Ertragseinbußen <10 %, Strom zusätzlich. Förderungen ab 2024 stark ausgebaut.",
    benefit: "Doppelnutzen pro Quadratmeter. Klimaschutz mit zweitem Einkommensstrom.",
    brands: ["Next2Sun", "BayWa r.e.", "Insolight"],
  },
  {
    id: "ki-schaedlingserkennung", era: "zukunft", category: "KI", title: "KI-Schaderreger-Früherkennung",
    icon: "Brain",
    gradient: "linear-gradient(135deg, #A04E6A 0%, #5A2438 100%)",
    summary: "Handykamera + KI erkennt Krankheiten und Schädlinge an den Blättern — in Sekunden.",
    details:
      "Convolutional-Neural-Networks wurden auf Millionen Beispielblättern trainiert. Ein Foto reicht, um Septoria, Mehltau, Rostkrankheiten oder Insekten-Befall mit >90 % Genauigkeit zu erkennen. Die App schlägt sofort eine schonende Behandlung vor.",
    benefit: "Früh-Entdeckung = weniger Spritzmittel. Demokratisierung von Fachwissen für jeden Hof.",
    brands: ["Plantix", "xarvio SCOUTING", "FarmVisor AI"],
  },
  {
    id: "digitaler-zwilling", era: "zukunft", category: "Daten", title: "Digitaler Zwilling des Hofs",
    icon: "Cube",
    gradient: "linear-gradient(135deg, #7E5CA4 0%, #3E2C52 100%)",
    summary: "Eine komplette Computer-Kopie deines Betriebs — mit allen Daten, Simulationen & Prognosen.",
    details:
      "Alle Maschinen-, Boden-, Tier- und Finanzdaten fließen in ein einziges 3D-Modell. Du kannst 'was wäre wenn'-Fragen simulieren: Was passiert mit dem Deckungsbeitrag, wenn ich auf Bio umstelle? Wie viel CO₂ spare ich mit Zwischenfrüchten? Der digitale Zwilling rechnet das durch.",
    benefit: "Komplexe Entscheidungen lassen sich erstmals datenbasiert und ganzheitlich treffen.",
    brands: ["365FarmNet", "AgriCircle", "Bodenblick (in Entwicklung)"],
  },
  {
    id: "drohnenschwaerme", era: "zukunft", category: "Robotik", title: "Autonome Drohnenschwärme",
    icon: "CirclesThree",
    gradient: "linear-gradient(135deg, #FF7A45 0%, #FF7A45 100%)",
    summary: "Mehrere Drohnen fliegen gleichzeitig, teilen sich das Feld auf und erledigen gezielte Aufgaben.",
    details:
      "Schwarmintelligenz erlaubt es, dass 5–10 kleine Drohnen ein 100-ha-Feld in <2 Stunden abfliegen, Unkräuter lokal besprühen oder Nützlinge aussetzen. In Japan schon Serienreif für Reis, in Europa ab 2026 im Hochwertkultur-Bereich erwartet.",
    benefit: "Extreme Präzision bei minimalem Chemie-Einsatz — und weniger Bodenverdichtung als Traktoren.",
    brands: ["DJI Agras", "XAG P100", "Hylio AgroDrone"],
  },
  {
    id: "vertikal-farming", era: "zukunft", category: "Kreislauf", title: "Vertical Farming & Hydroponik",
    icon: "Stack",
    gradient: "linear-gradient(135deg, #4A7A5C 0%, #204530 100%)",
    summary: "Gemüse in Etagen — 95 % weniger Wasser, 365 Tage Ernte, mitten in der Stadt.",
    details:
      "Salate, Kräuter und Microgreens wachsen in vertikalen Wasser-Kreisläufen unter LED-Licht. Ein Hektar Grundfläche kann so 10–20 ha Außenertrag ersetzen. Erste Kooperationen mit Landwirten als 'zweites Standbein' entstehen.",
    benefit: "Keine Wetterabhängigkeit, keine Pflanzenschutzmittel nötig, Lieferkette nur einige Kilometer.",
    brands: ["Infarm", "&ever", "Plenty", "Vertical Field"],
  },
  {
    id: "biochar", era: "zukunft", category: "Klimaschutz", title: "Pflanzenkohle (Biochar)",
    icon: "Flame",
    gradient: "linear-gradient(135deg, #5A3A28 0%, #2D1C14 100%)",
    summary: "Ernterückstände werden zu Kohle — der Kohlenstoff bleibt 1000+ Jahre im Boden.",
    details:
      "Langsame Pyrolyse bei 500–700 °C verwandelt Stroh, Maisspindel oder Holz in Pflanzenkohle. Ausgebracht im Boden speichert sie CO₂ dauerhaft, verbessert die Wasserhaltefähigkeit und bindet Nährstoffe. CO₂-Zertifikate (EBC, puro.earth) können als zusätzliches Einkommen verkauft werden.",
    benefit: "Doppelter Gewinn: bessere Böden + verkaufbare CO₂-Zertifikate (100–300 €/t CO₂).",
    brands: ["Pyreg", "Carbofex", "Sonnenerde"],
  },
];
